import {
	AddressEntity,
	CustomerAuditEntity,
	CustomerEntity,
	GroupEntity,
	GroupsCustomers,
} from 'Models/Entities';

import useAsync from 'Hooks/useAsync';
import useHasChanged from 'Hooks/useHasChanged';
import useStore from 'Hooks/useStore';
import BusinessDetailEntity from 'Models/Entities/BusinessDetailEntity';
import { acnClassOptions, acnSubClassOptions } from 'Models/Enums';
import HandleEvents from 'Util/HandleEvents';
import { DateFormat } from 'Util/StringUtils';
import alertToast from 'Util/ToastifyUtils';
import { AbnInput } from 'Views/Components/BusinessNumberInputs/AbnInput';
import { AcnInput } from 'Views/Components/BusinessNumberInputs/AcnInput';
import { BusinessValidation } from 'Views/Components/BusinessNumberInputs/BusinessValidationModel';
import { Colors, Display } from 'Views/Components/Button/Button';
import ButtonAsyncState from 'Views/Components/Button/ButtonAsyncState';
import { DatePicker } from 'Views/Components/DatePicker/DatePicker';
import If from 'Views/Components/If/If';
import { TextField } from 'Views/Components/TextBox/TextBox';
import { Tooltip } from 'Views/Components/Tooltip/Tooltip';
import classNames from 'classnames';
import { debounce, isEqual } from 'lodash';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment';
import { useCallback, useState } from 'react';
import { CreateCustomerAudit, GetCustomerAuditsDescription } from './CustomerAudits/Gibs/FetchCustomerAudits';
import { CustomerMonitoredDirectorInfo } from './DirectorSection/CustomerMonitoredDirectorInfo';
import GroupSelector from './GroupSelector';
import LiquorLicenceInformation from './LiquorLicenceSection/LiquorLicenceInformation';

// The modifiable fields on the Business Information Tab
const CustomerModifiableFields = ['abn', 'acn', 'trusteeACN', 'businessName', 'debtorID', 'firstName', 'lastName', 'dateOfBirth', 'groupss', 'drinksLicence', 'drinksLicenceState', 'drinksLicenceInfo'];

export interface CustomerDescription {
	title: string,
	message: string | null,
}

export interface BusinessInformationProps {
	originalCustomer: CustomerEntity,
	originalAbnAcnDetail?: BusinessDetailEntity;
	originalTrusteeAcnDetail?: BusinessDetailEntity;
}

const InitSectionsToShow = {
	abnInfo: true,
	asicInfo: true,
	liquorLicenceInfo: true,
	directorInfo: true,
};

const BusinessInformation = observer((props: BusinessInformationProps) => {
	const { originalCustomer, originalAbnAcnDetail, originalTrusteeAcnDetail } = props;
	const store = useStore();

	const [sectionsToShow, setSectionsToShow] = useState(InitSectionsToShow)

	const [abnacnDetail,] = useState<BusinessValidation>(new BusinessValidation({
		requiredAbn: true,
		abn: originalCustomer.abn,
		acn: originalCustomer.acn
	}));
	const [trusteeAcnDetail] = useState<BusinessValidation>(new BusinessValidation({
		acn: originalCustomer.trusteeACN
	}));

	const [customer, setPrivateCustomer] = useState(new CustomerEntity(originalCustomer));

	const comparatorCallback = useCallback((oldValue, newValue) => {
		// check if the object is an array and has the property 'groupsId'
		if (Array.isArray(oldValue) && Array.isArray(newValue)
			&& oldValue.filter(x => Object.prototype.hasOwnProperty.call(x, 'groupsId')).length === oldValue.length
			&& newValue.filter(x => Object.prototype.hasOwnProperty.call(x, 'groupsId')).length === newValue.length) {

			// if we are comparing Groups we compare that the array is the same set of group Ids
			return !isEqual(oldValue.map(x => x.groupsId).sort(), newValue.map(x => x.groupsId).sort(),);
		}
		return !isEqual(oldValue, newValue) && !(!oldValue && !newValue);
	}, []);

	const customerHasntChanged = useHasChanged(
		CustomerEntity,
		customer,
		CustomerModifiableFields,
		(oldValue, newValue) => comparatorCallback(oldValue, newValue),
		['errors'],
		(original, changed) => Object.keys(changed.errors).length > 0
	);

	const saveDisabled = () => {
		const businessNumberIsInvalid = (Object.keys(abnacnDetail.errors).length > 0) || (Object.keys(trusteeAcnDetail.errors).length > 0);
		return customerHasntChanged || businessNumberIsInvalid
	}

	// run liquor license validation on component mount
	const _ = useAsync(async () => {
		await customer.runLiquorLicenceValidation();
	}, []);

	const validateDebtorId = useCallback(async () => {
		// if the debtorID matches what the current saved ID, we know it must be valid
		if (originalCustomer.debtorID === customer.debtorID) {
			customer.setFieldError('debtorID', false, '');
			return;
		}
		await customer.validateField('debtorID');
	}, [originalCustomer, customer]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedValidateDebtorId = useCallback(debounce(validateDebtorId, 300), [validateDebtorId]);

	const onSave = useCallback(async () => {
		customer.customerAuditss = [];

		// Add the customer audits for the changed sections.
		const customerAuditsDescription = GetCustomerAuditsDescription(
			originalCustomer,
			customer,
			'BUSINESS_INFORMATION',
		);

		if (customerAuditsDescription !== undefined) {
			const newCustomerAudit = CreateCustomerAudit(
				originalCustomer.id,
				'BUSINESS_INFORMATION',
				customerAuditsDescription,
			);
			customer.customerAuditss.push(newCustomerAudit);
		}

		try {
			// Set relation path without group entity
			const relationPath: { [key: string]: any } = {
				physicalAddress: {},
				postalAddress: {},
				customerAuditss: {},
			};

			// add groups to relation path if a new association between customer and group was created
			// or an associate was removed. There's no way a groupss information changes, as either a
			// new groupss is created or removed
			if (customer.groupss.some(gc => gc.id === undefined) || customer
				.groupss.length !== originalCustomer.groupss.length) {
				relationPath.groupss = {};
			}

			// Add groups to relationPath if a new group entity is created
			if (customer.groupss.some(gc => gc.groups.id === undefined)) {
				relationPath.groupss = {
					groups: {},
				};
			}

			await customer.saveWithFetchBack(relationPath, {
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'groupss',
						],
					},
				],
			}, `
					physicalAddress {
						${AddressEntity.getAttributes().join('\n')}
					}
					postalAddress {
						${AddressEntity.getAttributes().join('\n')}
					}
					groupss {
						${GroupsCustomers.getAttributes().join('\n')}
						groups {
							${GroupEntity.getAttributes().join('\n')}
						}
					}
					customerAuditss {
						${CustomerAuditEntity.getAttributes().join('\n')}
					}
				`);
			const newCustomer = new CustomerEntity(customer);
			setPrivateCustomer(newCustomer);
			originalCustomer.groupss.length = 0;
			originalCustomer.assignAttributes(newCustomer);
			alertToast('Business information saved', 'success');
		} catch {
			alertToast('Business information could not be saved, please refresh and try again', 'error');
		}
	}, [originalCustomer, customer]);

	const onChevronClick = (field: ('abnInfo' | 'asicInfo' | 'liquorLicenceInfo' | 'directorInfo')) => {
		setSectionsToShow(old => ({
			...old,
			[field]: !old[field]
		}));
	};

	const editDisabled = store.userPermissions.intelManageCustomerBusinessInformation !== true;
	const editGroupDisabled = store.userPermissions.intelManageCustomerGroups !== true;

	const updateTrusteeAcnError = () => {
		// Set error for matching acn/trustee acn if it exists
		if (customer.trusteeACN?.length === 9 && customer.acn === customer.trusteeACN) {
			trusteeAcnDetail.setFieldError('acn', true, 'ACN and Trustee ACN can not match');
			// Clear the specific error if its valid (we don't want to override the predefined acn errors)
		} else if (trusteeAcnDetail.errors['acn'] === 'ACN and Trustee ACN can not match') {
			delete trusteeAcnDetail.errors['acn'];
		}
	};

	const onAfterAbnAcnChange = async (newValue: BusinessValidation) => {
		runInAction(() => {
			//@ts-ignore
			[customer.abn, customer.acn] = [newValue.abn, newValue.acn];
			updateTrusteeAcnError();
		});
		await customer.validateAddCustomerFields();
	}

	const onAfterTrusteeAcnChange = (newValue: BusinessValidation) => {
		runInAction(() => {
			// @ts-ignore
			customer.trusteeACN = newValue.acn;
			updateTrusteeAcnError();
		})
	}

	return (
		<div className="business-information">
			<h4>
				Business information
				<Tooltip
					id="abr-sync-tooltip"
					content="Where applicable, current data is synced from ABR and ASIC"
				/>
			</h4>
			<div className="inputs">
				<div className="inputs-column">
					<div className="input-container">
						<TextField
							model={customer}
							modelProperty="businessName"
							isDisabled={editDisabled}
							label="Business name"
						/>
					</div>

					<div className="input-container">
						<TextField
							model={customer}
							modelProperty="debtorID"
							errors={customer.errors.debtorID}
							onChangeAndBlur={validateDebtorId}
							onAfterChange={debouncedValidateDebtorId}
							label="Debtor ID"
							placeholder="Your customer reference"
							isDisabled={editDisabled}
						/>
					</div>
					<div className="input-container">
						<GroupSelector customer={customer} isDisabled={editGroupDisabled} />
					</div>
				</div>
				<div className="inputs-column">
					<div className="input-container">
						<TextField
							model={customer}
							modelProperty="firstName"
							isDisabled={editDisabled}
							label="First name"
							placeholder="Enter first name"
							tooltip='First name of the sole trader if the business is operated by an individual'
						/>
					</div>
					<div className="input-container">
						<TextField
							model={customer}
							modelProperty="lastName"
							isDisabled={editDisabled}
							label="Last name"
							placeholder="Enter last name"
							tooltip='Last name of the sole trader if the business is operated by an individual'
						/>
					</div>
					<div className="input-container">
						<DatePicker
							model={customer}
							modelProperty="dateOfBirth"
							label="Date of Birth"
							placeholder="Date of Birth"
						/>
					</div>
				</div>
			</div>

			<div className="section-title-container">
				<h6 className="section-title">ABR Information</h6>
				<span
					className={classNames('accordion-icon icon-only icon-chevron-down', {
						open: sectionsToShow.abnInfo,
					})}
					{...HandleEvents(() => onChevronClick('abnInfo'))}
				/>
			</div>

			<If condition={sectionsToShow.abnInfo}>
				<p className="type-title">ABN information</p>
				<div className="inputs">
					<div className="inputs-column stretch">
						<div className="input-container">
							<AbnInput model={abnacnDetail} isRequired onAfterChange={onAfterAbnAcnChange} />
						</div>
					</div>

					<div className="inputs-column">
						<ReadonlyModelInput
							value={!originalAbnAcnDetail?.abnActiveSince ? '-' : moment(originalAbnAcnDetail.abnActiveSince).format(DateFormat.UxFriendlyDate)}
							propertyName='abnActiveSince'
							label='Active Since'
						/>

						<ReadonlyModelInput
							value={!originalAbnAcnDetail?.abnLastUpdated
								? '-'
								: moment(originalAbnAcnDetail.abnLastUpdated).format(DateFormat.UxFriendlyDate)}
							propertyName='abnLastUpdated'
							label='Record updated '
						/>

						<ReadonlyModelInput
							value={!originalAbnAcnDetail?.abnRegisteredForGstFrom
								? '-'
								: `Registered from ${moment(originalAbnAcnDetail?.abnRegisteredForGstFrom).format(DateFormat.UxFriendlyDate)}`}
							propertyName='abnRegisteredForGstFrom'
							label="Goods & Services Tax (GST)"
						/>

						<ReadonlyModelInput
							value={originalAbnAcnDetail?.abnLocality ?? "-"}
							propertyName='abnLocality'
							label="Locality"
						/>

					</div>
				</div>
			</If>

			<div className="section-title-container">
				<h6 className="section-title">ASIC Information</h6>
				<span
					className={classNames('accordion-icon icon-only icon-chevron-down', {
						open: sectionsToShow.asicInfo,
					})}
					{...HandleEvents(() => onChevronClick('asicInfo'))}
				/>
			</div>

			<If condition={sectionsToShow.asicInfo}>
				<p className="type-title">ACN information</p>
				<div className="asic-acn inputs">
					<div className="inputs-column stretch">
						<div className="input-container">
							<AcnInput model={abnacnDetail} onAfterChange={onAfterAbnAcnChange} />
						</div>
					</div>

					<div className="inputs-column">
						<ReadonlyModelInput
							value={!originalAbnAcnDetail?.acnRegistrationDate ? '-' : moment(originalAbnAcnDetail.acnRegistrationDate).format(DateFormat.UxFriendlyDate)}
							propertyName='acnRegistrationDate'
							label='Date of registration'
						/>

						<ReadonlyModelInput
							value={!originalAbnAcnDetail?.acnClass ? '-' : acnClassOptions[originalAbnAcnDetail.acnClass]}
							propertyName='acnClass'
							label='Class '
						/>

						<ReadonlyModelInput
							value={!originalAbnAcnDetail?.acnSubClass ? '-' : acnSubClassOptions[originalAbnAcnDetail.acnSubClass]}
							propertyName='acnSubClass'
							label='Sub Class'
						/>
					</div>
				</div>

				<p className="type-title">Trustee ACN information</p>
				<div className="asic-trustee-acn inputs">
					<div className="inputs-column stretch">
						<div className="input-container">
							<AcnInput
								label='Trustee Australian Company Number (ACN)'
								model={trusteeAcnDetail}
								onAfterChange={onAfterTrusteeAcnChange}
							/>

						</div>
					</div>

					<div className="inputs-column">

						<ReadonlyModelInput
							value={!originalTrusteeAcnDetail?.acnRegistrationDate ? '-' : moment(originalTrusteeAcnDetail.acnRegistrationDate).format(DateFormat.UxFriendlyDate)}
							propertyName='acnRegistrationDate'
							label='Date of registration'
						/>

						<ReadonlyModelInput
							value={!originalTrusteeAcnDetail?.acnClass ? '-' : acnClassOptions[originalTrusteeAcnDetail.acnClass]}
							propertyName='acnClass'
							label='Class '
						/>

						<ReadonlyModelInput
							value={!originalTrusteeAcnDetail?.acnSubClass ? '-' : acnSubClassOptions[originalTrusteeAcnDetail.acnSubClass]}
							propertyName='acnSubClass'
							label='Sub Class'
						/>
					</div>
				</div>
			</If>

			{(!!originalCustomer.acn || !!originalCustomer.trusteeACN) && (<CustomerMonitoredDirectorInfo customerId={customer.id} />)}
			<If condition={customer.businessEntity.enableDrinksLicence}>
				<div className="section-title-container">
					<h6 className="section-title">Liquor Licence Information</h6>
					<span
						className={classNames('accordion-icon icon-only icon-chevron-down', {
							open: sectionsToShow.liquorLicenceInfo,
						})}
						{...HandleEvents(() => onChevronClick('liquorLicenceInfo'))}
					/>
				</div>

				<If condition={sectionsToShow.liquorLicenceInfo}>
					{/* eslint-disable-next-line max-len */}
					<LiquorLicenceInformation privateCustomer={customer} editDisabled={editDisabled} />
				</If>
			</If>

			<ButtonAsyncState
				colors={Colors.Primary}
				display={Display.Solid}
				onPress={onSave}
				readonly={saveDisabled()}
				className="save"
			>
				Save changes
			</ButtonAsyncState>
		</div>
	);
});

export default BusinessInformation;

/**
 * Readonly text input on a model property
 */
function ReadonlyModelInput(props: {
	label: string;
	propertyName: string;
	value: string;
}) {
	const {
		label,
		propertyName,
		value
	} = props;

	const model = {
		[propertyName]: value
	}

	return (
		<div className="input-container">
			<TextField
				model={model}
				modelProperty={propertyName}
				isDisabled
				label={label}
			/>
		</div>
	)
}
